import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Printer,
  FileText,
  Clock,
  History,
  GitCompare,
  Package,
  Truck,
  Send,
  DollarSign,
  Undo,
  Paperclip,
  Map,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import QRCode from 'qrcode';
import { StatusPill } from '../ui/gridCell';
import { ensureStatusLookup } from '../../services/statusLookup';
import Barcode from 'react-barcode';
import OrderDeliveryMap from './OrderDeliveryMap';

const OrderDetails = ({ orderId: orderIdProp, embedded = false, initialTab = 0 } = {}) => {
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams();
  const orderId = orderIdProp ?? orderIdParam;
  const [activeTab, setActiveTab] = useState(initialTab);

  const openHistoryComparison = () => {
    if (!orderId) return;
    navigate(`/home/orders/${orderId}/history-comparison`);
  };
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [remarks, setRemarks] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // History data
  const [statusHistory, setStatusHistory] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [pickupHistory, setPickupHistory] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [shipmentHistory, setShipmentHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [returnHistory, setReturnHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);

  // Attachment upload
  const [attachmentRemarks, setAttachmentRemarks] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState('');

  useEffect(() => {
    ensureStatusLookup();
  }, []);

  useEffect(() => {
    if (!orderId) return;
    if (!embedded) {
      window.scrollTo(0, 0);
    }
    setActiveTab(Number.isFinite(Number(initialTab)) ? Number(initialTab) : 0);
    setStatusHistory([]);
    setOrderHistory([]);
    setExpandedHistoryId(null);
    setPickupHistory([]);
    setDeliveryHistory([]);
    setShipmentHistory([]);
    setPaymentHistory([]);
    setReturnHistory([]);
    setAttachments([]);
    setAttachmentRemarks('');
    setSelectedFile(null);
    setAttachmentPreview('');
    loadOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, initialTab]);

  useEffect(() => {
    if (orderDetail && orderDetail.VoucherNo) {
      generateQRCode(orderDetail.VoucherNo);
    }
  }, [orderDetail]);

  useEffect(() => {
    if (activeTab === 1 && statusHistory.length === 0) {
      loadStatusHistory();
    } else if (activeTab === 2 && orderHistory.length === 0) {
      loadOrderHistory();
    } else if (activeTab === 3 && pickupHistory.length === 0) {
      loadPickupHistory();
    } else if (activeTab === 4 && deliveryHistory.length === 0) {
      loadDeliveryHistory();
    } else if (activeTab === 5 && shipmentHistory.length === 0) {
      loadShipmentHistory();
    } else if (activeTab === 6 && paymentHistory.length === 0) {
      loadPaymentHistory();
    } else if (activeTab === 7 && returnHistory.length === 0) {
      loadReturnHistory();
    } else if (activeTab === 8 && attachments.length === 0) {
      loadAttachments();
    }
  }, [activeTab]);

  const generateQRCode = async (voucherNo) => {
    try {
      const url = await QRCode.toDataURL(voucherNo, {
        width: 128,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrderById(orderId);
      setOrderDetail(response);
      setRemarks(response.Remarks || '');
    } catch (err) {
      console.error('Error loading order details:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const loadStatusHistory = async () => {
    try {
      const response = await MainService.OrderStatusByOrderId(orderId);
      setStatusHistory(response || []);
    } catch (err) {
      console.error('Error loading status history:', err);
    }
  };

  const loadOrderHistory = async () => {
    try {
      const response = await MainService.OrderHistoryByOrderId(orderId);
      setOrderHistory(response || []);
    } catch (err) {
      console.error('Error loading order history:', err);
    }
  };

  const loadPickupHistory = async () => {
    try {
      const response = await MainService.GetPickupNoteDetailsByOrderId(orderId);
      setPickupHistory(response || []);
    } catch (err) {
      console.error('Error loading pickup history:', err);
    }
  };

  const loadDeliveryHistory = async () => {
    try {
      const response = await MainService.GetDeliveryNoteDetailsByOrderId(orderId);
      setDeliveryHistory(response || []);
    } catch (err) {
      console.error('Error loading delivery history:', err);
    }
  };

  const loadShipmentHistory = async () => {
    try {
      const response = await MainService.getShipmentByOrderId(orderId);
      setShipmentHistory(response || []);
    } catch (err) {
      console.error('Error loading shipment history:', err);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      const response = await MainService.getClientPaymentByOrderId(orderId);
      setPaymentHistory(response || []);
    } catch (err) {
      console.error('Error loading payment history:', err);
    }
  };

  const loadReturnHistory = async () => {
    try {
      const response = await MainService.getClientReturnByOrderId(orderId);
      setReturnHistory(response || []);
    } catch (err) {
      console.error('Error loading return history:', err);
    }
  };

  const loadAttachments = async () => {
    try {
      const response = await MainService.OrderAttachments(orderId);
      setAttachments(response || []);
    } catch (err) {
      console.error('Error loading attachments:', err);
    }
  };

  const handleUpdateRemarks = async () => {
    try {
      setLoading(true);
      const updatedOrder = { ...orderDetail, Remarks: remarks };
      const response = await MainService.UpdateOrderRemarks(updatedOrder);
      if (response) {
        setSuccess('Remarks updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update remarks');
      }
    } catch (err) {
      console.error('Error updating remarks:', err);
      setError('Failed to update remarks');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachmentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveAttachment = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('OrderId', orderId);
      formData.append('Remarks', attachmentRemarks);
      formData.append('Photo', selectedFile);

      const response = await MainService.SaveOrderAttachment(formData);
      if (response) {
        setSuccess('Attachment saved successfully');
        setAttachmentRemarks('');
        setSelectedFile(null);
        setAttachmentPreview('');
        setTimeout(() => setSuccess(''), 3000);
        loadAttachments();
      } else {
        setError('Failed to save attachment');
      }
    } catch (err) {
      console.error('Error saving attachment:', err);
      setError('Failed to save attachment');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    const printOrderId = orderDetail?.Id || orderId;
    if (!printOrderId) {
      setError('Print error: Order ID not found');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.PrintOrder(printOrderId);
      const filePath = typeof response === 'string' ? response : (response?.data || response);

      if (filePath) {
        const fullUrl = String(filePath).startsWith('http')
          ? filePath
          : `${getApiBaseUrl()}ReportFiles/${filePath}`;
        window.open(fullUrl, '_blank');
      } else {
        setError('Print error: No file path returned');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError('Print error');
    } finally {
      setLoading(false);
    }
  };

  // Check if URL is a Google Maps link
  const isGoogleMapsUrl = (url) => {
    if (!url || url === 'N/A') return false;
    const urlStr = url.toString().toLowerCase();
    return urlStr.includes('maps.google') || urlStr.includes('goo.gl/maps') || urlStr.includes('maps.app.goo.gl');
  };

  // Reusable field component
  const FieldRow = ({ label, value, labelWidth = '120px', isLink = false }) => {
    const renderValue = () => {
      // Keep numeric 0 visible (e.g. StoreRefNo / Attempts); only blank-like values → N/A
      if (value === null || value === undefined || value === '' || value === 'N/A') return 'N/A';
      
      if (isLink && isGoogleMapsUrl(value)) {
        return (
          <Box
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#46955A',
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              '&:hover': { color: '#3d7d4f' }
            }}
          >
            {value}
          </Box>
        );
      }
      
      return (
        <Box component="span" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {value}
        </Box>
      );
    };

    return (
      <Typography variant="body2" sx={{ display: 'flex', gap: 1 }}>
        <Box component="span" sx={{ minWidth: labelWidth, fontWeight: 500 }}>{label}:</Box>
        {renderValue()}
      </Typography>
    );
  };

  // Common styles
  const gridItemStyle = { flex: 1, minWidth: 0, overflow: 'hidden' };
  const sectionBoxStyle = { borderRight: { md: '1px solid #e0e0e0' }, pr: { md: 3 }, overflow: 'hidden' };
  const fieldContainerStyle = { display: 'flex', flexDirection: 'column', gap: 1 };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB') + ' at ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading && !orderDetail) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !orderDetail) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!orderDetail) return null;

  const tabs = [
    { label: 'Order Info', icon: FileText },
    { label: 'Status Updates', icon: Clock },
    { label: 'Order History', icon: History },
    { label: 'Pickup', icon: Package },
    { label: 'Delivery', icon: Truck },
    { label: 'Shipping', icon: Send },
    { label: 'Payments', icon: DollarSign },
    { label: 'Returns', icon: Undo },
    { label: 'Attachments', icon: Paperclip },
    { label: 'Delivery Map', icon: Map },
    { label: 'History Comparison', icon: GitCompare, navigateTo: 'historyComparison' },
  ];

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: embedded ? 0 : 3, boxShadow: embedded ? 'none' : undefined }}>
        <CardContent>
          {!embedded && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Order Details
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<GitCompare size={16} />}
                  onClick={openHistoryComparison}
                  sx={{
                    borderColor: '#46955A',
                    color: '#46955A',
                    '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
                  }}
                >
                  History Comparison
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Printer />}
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Box>
            </Box>
          )}

          {/* Success/Error Messages */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              if (tabs[newValue]?.navigateTo === 'historyComparison') {
                openHistoryComparison();
                return;
              }
              setActiveTab(newValue);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                color: '#6B7280',
                '&.Mui-selected': {
                  color: '#46955A'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#46955A'
              }
            }}
          >
            {tabs.map((tab, index) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={<Icon size={16} />}
                  iconPosition="start"
                />
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Tab Content */}
      <Card>
        <CardContent>
          {/* Order Info Tab */}
          {activeTab === 0 && (
            <Box>
              {/* First Row: QR Code, Order Info, Payment Status, Pricing Details */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Box display="flex" gap={2} mb={2} flexDirection="column">
                      {qrCodeUrl && (
                        <Box>
                          <img src={qrCodeUrl} alt="QR Code" style={{ width: '128px', height: '128px' }} />
                        </Box>
                      )}
                      {orderDetail.VoucherNo && (
                        <Box>
                          <Barcode value={orderDetail.VoucherNo} format="CODE128" />
                        </Box>
                      )}
                    </Box>
                    <Box mt={1}>
                      <StatusPill status={orderDetail.Status} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Voucher No" value={orderDetail.VoucherNo} />
                      <FieldRow label="Client Bill No" value={orderDetail.ClientBillNo} />
                      <FieldRow label="Store Ref No" value={orderDetail.StoreRefNo} />
                      <FieldRow label="Order Date" value={formatDate(orderDetail.Date)} />
                      <FieldRow label="Pickup Date" value={formatDate(orderDetail.PickupDate)} />
                      <FieldRow label="Delivery Date" value={formatDate(orderDetail.DeliveryDate)} />
                      <FieldRow label="Pay Type" value={orderDetail.PayType} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Payment Status" value={orderDetail.PaymentStatus} />
                      <FieldRow label="Return Status" value={orderDetail.ReturnStatus} />
                      <FieldRow label="Created By" value={orderDetail.CreatedBy?.UserName} />
                      <FieldRow label="Updated By" value={orderDetail.UpdatedBy?.UserName} />
                      <FieldRow label="Attempts" value={orderDetail.Attempts} />
                      <FieldRow label="Reason" value={orderDetail.Reason?.Description} />
                      <FieldRow label="Batch No" value={orderDetail.BatchNo} />
                      <FieldRow label="Picked By" value={orderDetail.PickUpBy?.Name} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Pricing Details</Typography>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Product Price" value={`${orderDetail.ProductPrice || 0} AED`} labelWidth="180px" />
                      <FieldRow label="Previous Product Price" value={`${orderDetail.ACTProductPrice || 0} AED`} labelWidth="180px" />
                      <FieldRow label="Actual Product Price" value={`${orderDetail.MainProductPrice || 0} AED`} labelWidth="180px" />
                      <FieldRow label="Delivery Charge" value={`${orderDetail.DeliveryCharge || 0} AED`} labelWidth="180px" />
                      <FieldRow label="Tax" value={`${orderDetail.TAX || 0} AED`} labelWidth="180px" />
                      <Typography variant="body2" sx={{ display: 'flex', gap: 1, fontWeight: 'bold' }}>
                        <Box component="span" sx={{ minWidth: '180px', fontWeight: 700 }}>Client Amount:</Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{orderDetail.ClientAmount || 0} AED</Box>
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', gap: 1, fontWeight: 'bold' }}>
                        <Box component="span" sx={{ minWidth: '180px', fontWeight: 700 }}>Customer Amount:</Box>
                        <Box component="span" sx={{ fontWeight: 700 }}>{orderDetail.CustomerAmount || 0} AED</Box>
                      </Typography>
                      <FieldRow label="Agent" value={orderDetail.Agent?.Name} labelWidth="180px" />
                      <FieldRow label="Commission" value={`${orderDetail.CommissionAmount || 0} AED`} labelWidth="180px" />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Address Sections: Shipper Details, Shipping Address, Consignee Details, Delivery Address */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Shipper Details</Typography>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Name" value={orderDetail.Client?.Name} />
                      <FieldRow label="Client No" value={orderDetail.Client?.ClientNo} />
                      <FieldRow label="Mobile No1" value={orderDetail.Client?.MobileNo1} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Shipping Address</Typography>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Contact No" value={orderDetail.ClientAddress?.ContactNo} />
                      <FieldRow label="Location" value={orderDetail.ClientAddress?.Location?.Name} />
                      <FieldRow label="Emirate" value={orderDetail.ClientAddress?.Emirate?.Name} />
                      <FieldRow label="Area" value={orderDetail.ClientAddress?.Location?.Area?.Name} />
                      <FieldRow label="Address" value={orderDetail.ClientAddress?.Address} />
                      <FieldRow label="PO Box" value={orderDetail.ClientAddress?.POBOX} />
                      <FieldRow label="Location URL" value={orderDetail.ClientAddress?.LocationUrl} isLink={true} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={sectionBoxStyle}>
                    <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Consignee Details</Typography>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Name" value={orderDetail.Customer?.Name} />
                      <FieldRow label="Mobile No1" value={orderDetail.Customer?.MobileNo1} />
                      <FieldRow label="Mobile No2" value={orderDetail.Customer?.MobileNo2} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} style={gridItemStyle}>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Delivery Address</Typography>
                    <Box sx={fieldContainerStyle}>
                      <FieldRow label="Location" value={orderDetail.CustomerAddress?.Location?.Name} />
                      <FieldRow label="Emirate" value={orderDetail.CustomerAddress?.Emirate?.Name} />
                      <FieldRow label="Area" value={orderDetail.CustomerAddress?.Location?.Area?.Name} />
                      <FieldRow label="Address" value={orderDetail.CustomerAddress?.Address} />
                      <FieldRow label="PO Box" value={orderDetail.CustomerAddress?.POBOX} />
                      <FieldRow label="Location URL" value={orderDetail.CustomerAddress?.LocationUrl} isLink={true} />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Remarks, Delivery Signature and Proof */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={8} style={{ flex: 2 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Remarks</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Button
                      variant="contained"
                      onClick={handleUpdateRemarks}
                      disabled={loading}
                      sx={{
                        backgroundColor: '#46955A',
                        '&:hover': { backgroundColor: '#3d7d4f' },
                        '&:disabled': { backgroundColor: '#9ca3af' }
                      }}
                    >
                      Save Remarks
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6} md={2} style={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Delivery Signature</Typography>
                  {orderDetail.Signature ? (
                    <img
                      src={orderDetail.Signature}
                      alt="Delivery Signature"
                      style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No signature available</Typography>
                  )}
                </Grid>
                <Grid item xs={6} md={2} style={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight="bold" mb={2} textAlign="left">Delivery Proof</Typography>
                  {orderDetail.Proof ? (
                    <img
                      src={orderDetail.Proof}
                      alt="Delivery Proof"
                      style={{ maxWidth: '100%', height: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">No proof available</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Status Updates Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={3}>Status Updates</Typography>
              {statusHistory.length === 0 ? (
                <Typography color="text.secondary">No status updates available</Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {/* Vertical connecting line - stops at last item */}
                  {statusHistory.length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '8px',
                        top: '8px',
                        height: `${(statusHistory.length - 1) * 96}px`,
                        width: '2px',
                        backgroundColor: '#46955A',
                        zIndex: 0
                      }}
                    />
                  )}
                  {statusHistory.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        mb: 4,
                        minHeight: '60px',
                        zIndex: 1
                      }}
                    >
                      {/* Circular dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: '-24px',
                          top: '4px',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          backgroundColor: '#46955A',
                          border: '2px solid #fff',
                          boxShadow: '0 0 0 2px #46955A',
                          zIndex: 2
                        }}
                      />
                      {/* Content */}
                      <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, color: '#111827', fontSize: '0.9375rem' }}>
                        {formatDateTime(item.Date)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                        {item.Status?.Name || 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Order History Tab */}
          {activeTab === 2 && (
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                <Typography variant="h6" fontWeight="bold">Order History</Typography>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<GitCompare size={16} />}
                  onClick={openHistoryComparison}
                  sx={{
                    borderColor: '#46955A',
                    color: '#46955A',
                    '&:hover': { borderColor: '#3d7d4f', backgroundColor: '#f0f9f4' },
                  }}
                >
                  History Comparison
                </Button>
              </Box>
              {orderHistory.length === 0 ? (
                <Typography color="text.secondary">No order history available</Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: 4 }}>
                  {orderHistory.length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '8px',
                        top: '8px',
                        bottom: '24px',
                        width: '2px',
                        backgroundColor: '#46955A',
                        zIndex: 0
                      }}
                    />
                  )}
                  {orderHistory.map((item, index) => {
                    const rowKey = item.Id ?? index;
                    const expanded = expandedHistoryId === rowKey;
                    const hasSnapshot = !!(item.VoucherNo || item.ClientName || item.CustomerName || item.ChargeFrom || item.PayType);
                    return (
                      <Box
                        key={rowKey}
                        sx={{
                          position: 'relative',
                          mb: 3,
                          zIndex: 1
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: '-24px',
                            top: '4px',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            backgroundColor: '#46955A',
                            border: '2px solid #fff',
                            boxShadow: '0 0 0 2px #46955A',
                            zIndex: 2
                          }}
                        />
                        <Box
                          sx={{
                            border: '1px solid #E5E7EB',
                            borderRadius: 2,
                            bgcolor: '#fff',
                            p: 2
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, color: '#111827', fontSize: '0.9375rem' }}>
                                {formatDateTime(item.Date)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', mb: 0.5 }}>
                                {item.Remarks || 'N/A'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.75rem' }}>
                                by {item.User?.UserName || 'N/A'}
                                {item.StatusName ? ` · ${item.StatusName}` : ''}
                                {item.VoucherNo ? ` · ${item.VoucherNo}` : ''}
                              </Typography>
                            </Box>
                            {hasSnapshot && (
                              <IconButton
                                size="small"
                                onClick={() => setExpandedHistoryId(expanded ? null : rowKey)}
                                aria-label={expanded ? 'Hide snapshot' : 'Show snapshot'}
                              >
                                {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </IconButton>
                            )}
                          </Box>

                          <Collapse in={expanded}>
                            <Divider sx={{ my: 1.5 }} />
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Order / Payment</Typography>
                                <FieldRow label="Voucher No" value={item.VoucherNo} />
                                <FieldRow label="Client Bill No" value={item.ClientBillNo} />
                                <FieldRow label="Charge From" value={item.ChargeFrom} />
                                <FieldRow label="Pay Type" value={item.PayType} />
                                <FieldRow label="Payment Status" value={item.PaymentStatus} />
                                <FieldRow label="Return Status" value={item.ReturnStatus} />
                                <FieldRow label="Payment Ref" value={item.PaymentRefNo} />
                                <FieldRow label="Return Ref" value={item.ReturnRefNo} />
                                <FieldRow label="Quantity" value={item.Quantity} />
                                <FieldRow label="Product Price" value={item.ProductPrice} />
                                <FieldRow label="Delivery Charge" value={item.DeliveryCharge} />
                                <FieldRow label="TAX" value={item.TAX} />
                                <FieldRow label="Customer Amount" value={item.CustomerAmount} />
                                <FieldRow label="Client Amount" value={item.ClientAmount} />
                                <FieldRow label="Store Ref" value={item.StoreRefNo} />
                                <FieldRow label="Type" value={item.TypeName} />
                                <FieldRow label="Agent" value={item.AgentName} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Dates / Flags</Typography>
                                <FieldRow label="Order Date" value={formatDate(item.OrderDate)} />
                                <FieldRow label="Pickup Date" value={formatDate(item.PickupDate)} />
                                <FieldRow label="Delivery Date" value={formatDate(item.DeliveryDate)} />
                                <FieldRow label="Pickup By" value={item.PickUpByName} />
                                <FieldRow label="Deliver By" value={item.DeliverByName} />
                                <FieldRow label="Status" value={item.StatusName || item.StatusKeyword} />
                                <FieldRow label="Is Pickup" value={item.IsPickup ? 'Yes' : 'No'} />
                                <FieldRow label="Is Exchange" value={item.IsExchange ? 'Yes' : 'No'} />
                                <FieldRow label="Is Fragile" value={item.IsFragile ? 'Yes' : 'No'} />
                                <FieldRow label="Is Free" value={item.IsFree ? 'Yes' : 'No'} />
                                <FieldRow label="Advance Pay" value={item.IsAdvancePay ? 'Yes' : 'No'} />
                                <FieldRow label="Advance Price" value={item.IsAdvancePrice ? 'Yes' : 'No'} />
                                <FieldRow label="Order Remarks" value={item.OrderRemarks} />
                                <FieldRow label="Driver Remarks" value={item.DriverRemarks} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Client</Typography>
                                <FieldRow label="Client No" value={item.ClientNo} />
                                <FieldRow label="Name" value={item.ClientName} />
                                <FieldRow label="Mobile" value={item.ClientMobile} />
                                <FieldRow label="Address" value={item.ClientAddress} />
                                <FieldRow label="Location" value={item.ClientLocationName} />
                                <FieldRow label="Emirate" value={item.ClientEmirateName} />
                                <FieldRow label="PO Box" value={item.ClientPOBOX} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Customer</Typography>
                                <FieldRow label="Name" value={item.CustomerName} />
                                <FieldRow label="Mobile" value={item.CustomerMobile} />
                                <FieldRow label="Mobile 2" value={item.CustomerMobile2} />
                                <FieldRow label="Address" value={item.CustomerAddress} />
                                <FieldRow label="Location" value={item.CustomerLocationName} />
                                <FieldRow label="Emirate" value={item.CustomerEmirateName} />
                                <FieldRow label="PO Box" value={item.CustomerPOBOX} />
                              </Grid>
                            </Grid>
                          </Collapse>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* Pickup Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" mb={3}>Pickup History</Typography>
              {pickupHistory.length === 0 ? (
                <Typography color="text.secondary">No pickup history available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Driver</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Updated By</TableCell>
                        <TableCell>Order Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pickupHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.PickUpNote?.RefNo || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(item.PickUpNote?.Date)}</TableCell>
                          <TableCell>{item.PickUpNote?.Status || 'N/A'}</TableCell>
                          <TableCell>{item.PickUpNote?.Driver?.Name || 'N/A'}</TableCell>
                          <TableCell>{item.PickUpNote?.CreatedName || 'N/A'}</TableCell>
                          <TableCell>{item.PickUpNote?.UpdatedName || 'N/A'}</TableCell>
                          <TableCell>{item.Status?.Name || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Delivery Tab */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" mb={3}>Delivery History</Typography>
              {deliveryHistory.length === 0 ? (
                <Typography color="text.secondary">No delivery history available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Driver</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Updated By</TableCell>
                        <TableCell>Order Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deliveryHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.DeliveryNote?.RefNo || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(item.DeliveryNote?.Date)}</TableCell>
                          <TableCell>{item.DeliveryNote?.Status || 'N/A'}</TableCell>
                          <TableCell>{item.DeliveryNote?.Driver?.Name || 'N/A'}</TableCell>
                          <TableCell>{item.DeliveryNote?.CreatedName || 'N/A'}</TableCell>
                          <TableCell>{item.DeliveryNote?.UpdatedName || 'N/A'}</TableCell>
                          <TableCell>{item.Status?.Name || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Shipping Tab */}
          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" mb={3}>Shipping History</Typography>
              {shipmentHistory.length === 0 ? (
                <Typography color="text.secondary">No shipping history available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Updated By</TableCell>
                        <TableCell>Delivery Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shipmentHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.RefNo || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(item.Date)}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.IsApproved ? 'Approved' : 'New'}
                              color={item.IsApproved ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{item.CreatedByName || 'N/A'}</TableCell>
                          <TableCell>{item.UpdatedByName || 'N/A'}</TableCell>
                          <TableCell>{item.DeliveryNote?.RefNo || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Payments Tab */}
          {activeTab === 6 && (
            <Box>
              <Typography variant="h6" mb={3}>Payment History</Typography>
              {paymentHistory.length === 0 ? (
                <Typography color="text.secondary">No payment history available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Created By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.RefNo || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(item.Date)}</TableCell>
                          <TableCell>{item.Status || 'N/A'}</TableCell>
                          <TableCell>{item.Employee?.Name || 'N/A'}</TableCell>
                          <TableCell>{item.Createdby?.UserName || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Returns Tab */}
          {activeTab === 7 && (
            <Box>
              <Typography variant="h6" mb={3}>Return History</Typography>
              {returnHistory.length === 0 ? (
                <Typography color="text.secondary">No return history available</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ref No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Employee</TableCell>
                        <TableCell>Created By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnHistory.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.RefNo || 'N/A'}</TableCell>
                          <TableCell>{formatDateTime(item.Date)}</TableCell>
                          <TableCell>{item.Status || 'N/A'}</TableCell>
                          <TableCell>{item.Employee?.Name || 'N/A'}</TableCell>
                          <TableCell>{item.Createdby?.UserName || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Attachments Tab */}
          {activeTab === 8 && (
            <Box>
              <Typography variant="h6" mb={3}>Attachments</Typography>

              {/* Upload Section */}
              <Card sx={{ mb: 3, p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Remarks"
                      value={attachmentRemarks}
                      onChange={(e) => setAttachmentRemarks(e.target.value)}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{
                        borderColor: '#46955A',
                        color: '#46955A',
                        '&:hover': {
                          borderColor: '#3d7d4f',
                          backgroundColor: '#f0f9f4'
                        }
                      }}
                    >
                      Select Photo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      onClick={handleSaveAttachment}
                      disabled={!selectedFile || loading}
                      fullWidth
                      sx={{
                        backgroundColor: '#46955A',
                        '&:hover': {
                          backgroundColor: '#3d7d4f'
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af'
                        }
                      }}
                    >
                      Save Attachment
                    </Button>
                  </Grid>
                </Grid>
                {attachmentPreview && (
                  <Box mt={2}>
                    <img
                      src={attachmentPreview}
                      alt="Preview"
                      style={{ maxWidth: '300px', height: 'auto', border: '1px solid #e0e0e0' }}
                    />
                  </Box>
                )}
              </Card>

              {/* Attachments List */}
              {attachments.length === 0 ? (
                <Typography color="text.secondary">No attachments available</Typography>
              ) : (
                <Grid container spacing={2}>
                  {attachments.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card>
                        <Box sx={{ position: 'relative', paddingTop: '75%' }}>
                          <img
                            src={item.Photo}
                            alt={item.Remarks || 'Attachment'}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(item.Date)}
                          </Typography>
                          <Typography variant="body2">{item.Remarks || 'No remarks'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            By {item.User?.UserName || 'N/A'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Delivery Map Tab */}
          {activeTab === 9 && (
            <Box>
              <Typography variant="h6" mb={2}>Delivery Map</Typography>
              <OrderDeliveryMap order={orderDetail} height={embedded ? 360 : 480} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderDetails;

